import styled from 'styled-components';
import React from 'react';
import Flickity from 'react-flickity-component';
import media from 'client/styles/media';

import PaddingContainer from './PaddingContainer';
import LinkButton from './LinkButton';

const flickityOptions = {
  prevNextButtons: false,
  pageDots: true,
  imagesLoaded: true,
  setGallerySize: true,
};

const GrowthSection = styled.section`
  position: relative;
  width: 100%;
  padding: 80px 0 180px;
  z-index: 0;
  h2 {
    ${({ theme }) => theme.typography.h1};
    margin: 0 0 30px;
  }

  .img-bg {
    position: absolute;
    z-index: -1;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-55%);
  }

  ${media.md} {
    padding: 80px 0 180px;
    h2 {
      ${({ theme }) => theme.typography.hero};
      margin: 0 0 45px;
    }
    ${PaddingContainer} {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      grid-column-gap: 16px;
    }
  }
`;

const GrowthText = styled.div`
  margin-bottom: 36px;
  ${media.md} {
    grid-column: 1 / span 4;
  }
  ${media.lg} {
    grid-column: 2 / span 4;
    padding-right: 42px;
  }
`;
const GrowthPoint = styled.div`
  position: relative;
  padding-left: 50px;
  img {
    position: absolute;
    left: 0;
    top: -4px;
  }
`;

const FlickityWrap = styled.div`
  margin: 0 -16px;
  ${media.md} {
    margin: 0;
  }
`;
const GrowthSlider = styled.div`
  position: relative;
  &:focus {
    outline: none;
  }

  .growth-slider {
    position: relative;
    background: #fff;
    padding: 20px 16px 5px;
    box-shadow: 0px 0px 20px rgba(37, 38, 94, 0.1);
    border-radius: 16px;
    margin: 0 0 40px;
    width: 100%;

    &:focus {
      outline: none;
    }
    &:before {
      content: '';
      display: block;
      width: 54px;
      height: 73px;
      position: absolute;
      top: -80px;
      right: 0;
      background: url(https://static.introwise.com/images/elements/main/logo_blue.svg) no-repeat;
      background-size: contain;
      z-index: -1;
    }
  }
  .flickity-slider {
    position: relative;
  }
  .flickity-viewport {
    width: 100%;
  }
  .slide {
    position: relative;
    width: 100%;
    padding-top: 58.22%;
    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
    }
  }
  .flickity-viewport {
    overflow: hidden;
  }
  .flickity-page-dots {
    list-style-type: none;
    text-align: center;
    padding: 0;
    margin: 16px 0;
    .dot {
      display: inline-block;
      height: 12px;
      width: 12px;
      background: url(https://static.introwise.com/images/elements/main/dot_blue_v2.svg) center center no-repeat;
      background-size: contain;
      margin: 0 7px;
      cursor: pointer;
      opacity: 0.2;
      &.is-selected {
        opacity: 1;
      }
    }
  }
  ${media.sm} {
    .growth-slider {
      padding: 30px 30px 5px;
      margin: 0 0 30px;
      &:before {
        top: -30px;
        right: -30px;
      }
    }
    .flickity-page-dots {
      margin: 30px 0 16px;
      .dot {
        height: 20px;
        width: 20px;
        margin: 0 13px;
      }
    }
  }
  ${media.md} {
    grid-column: 5 / span 8;
    .growth-slider {
      &:before {
        width: 75px;
        height: 102px;
        left: -46px;
        top: -60px;
      }
    }
  }
  ${media.lg} {
    grid-column: 6 / span 6;
    margin-left: -16px;
  }
`;
const GrowthSliderCaption = styled.div`
  p {
    margin: 0 0 6px;
  }
  .small {
    ${({ theme }) => theme.typography.small};
  }
  ${LinkButton} {
    margin-top: 20px;
  }

  ${media.md} {
    position: relative;
    padding-right: 150px;
    ${LinkButton} {
      position: absolute;
      right: 0;
      top: 0;
      margin-top: 0;
    }
  }
`;

const Growth = () => (
  <GrowthSection id="growth">
    <img className="img-bg" src="https://static.introwise.com/images/elements/main/growth_bg.svg" alt="" />
    <PaddingContainer>
      <GrowthText>
        <h2>Growth</h2>
        <GrowthPoint>
          <img src="https://static.introwise.com/images/elements/main/dot_red.svg" alt="" />
          <p>Help communities grow bigger through events</p>
        </GrowthPoint>
        <GrowthPoint>
          <img src="https://static.introwise.com/images/elements/main/dot_yellow.svg" alt="" />
          <p>Give each member a way to pay it forward and a sense of belonging</p>
        </GrowthPoint>
        <GrowthPoint>
          <img src="https://static.introwise.com/images/elements/main/dot_blue.svg" alt="" />
          <p>Build involved and loyal audience that keeps coming back</p>
        </GrowthPoint>
        <GrowthPoint>
          <img src="https://static.introwise.com/images/elements/main/dot_pink.svg" alt="" />
          <p>Encourage participation with the help of smart recommendations eliminating spam</p>
        </GrowthPoint>
      </GrowthText>
      <GrowthSlider>
        <FlickityWrap>
          <Flickity
            className="growth-slider"
            elementType="div"
            options={flickityOptions}
            disableImagesLoaded={false}
            reloadOnUpdate
          >
            <div className="slide">
              <img src="https://static.introwise.com/images/elements/main/growth_slide_1.jpg" alt="" />
            </div>
            <div className="slide">
              <img src="https://static.introwise.com/images/elements/main/growth_slide_2.jpg" alt="" />
            </div>
            <div className="slide">
              <img src="https://static.introwise.com/images/elements/main/growth_slide_3.jpg" alt="" />
            </div>
            <div className="slide">
              <img src="https://static.introwise.com/images/elements/main/growth_slide_4.jpg" alt="" />
            </div>
            <div className="slide">
              <img src="https://static.introwise.com/images/elements/main/growth_slide_5.jpg" alt="" />
            </div>
          </Flickity>
        </FlickityWrap>
        <GrowthSliderCaption>
          <p>As a community manager you can</p>
          <p className="small">
            See members activity, identify community champions and hot topics, create public events to grow you
            community
          </p>
          <LinkButton variant="blueLight" to="/communities-intro">
            Learn more
          </LinkButton>
        </GrowthSliderCaption>
      </GrowthSlider>
    </PaddingContainer>
  </GrowthSection>
);

export default Growth;
